import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CloudUpload, FileText, Map as MapIcon, Table2 } from 'lucide-react';
import { AddAction } from '../components/add-action';
import { AreaEditor } from '../components/area-editor';
import { BuildingEditor } from '../components/building-editor';
import { BuildingList } from '../components/building-list';
import { AreaPhotoMissing } from '../components/area-photo';
import { ConditionPanel } from '../components/condition-panel';
import { ConditionReportPanel } from '../components/condition-report-panel';
import { ConditionScale } from '../components/condition-scale';
import { ConditionStart } from '../components/condition-start';
import { VisitPanel } from '../components/visit-panel';
import { DefectsSection } from '../components/defects-section';
import { EvidencePointsSection } from '../components/evidence-points-section';
import { PhotosFolderLink } from '../components/photos-folder-link';
import { SalesRiskLink } from '../components/sales-risk-link';
import { SyncStatus } from '../components/sync-status';
import { MeasureStatusBar } from '../components/measure-status-bar';
import { advanceMeasure } from '../lib/measure-status-save';
import { formatHours } from '../components/surface-format';
import { areaColour, areaRating, ratingText, scaleKeyOf } from '../lib/condition';
import { addEarlierPhotos, observationsOf, visitAreaOf, visitOf } from '../lib/condition-records';
import { WashOnlyBadge } from '../components/area-wash';
import { siteWash } from '../lib/site-wash';
import { visitLocked } from '../lib/visit-steps';
import type { PhotoSources } from '../lib/area-photos';
import { buildingGroups, differingFields, measureBuildings, newBuildingFields, nextAreaOrder, reorderBuildings } from '../lib/buildings';
import { getJob, listJobPhotos, listRecords, patchFields, recordOpen, type DeviceRecord } from '../lib/device-db';
import { useDeviceQuery, useJobRefresh } from '../lib/hooks';
import { addArea, addBuilding, duplicateArea, openJobById, removeArea, removeBuilding } from '../lib/jobs';

/** The job page's tabs, at the top (DD, 2026-10-21): switching keeps the same area selected. */
/** The Condition tab's Report section, chosen like an area in the list. */
const REPORT_SECTION = 'report';

const TABS = [
  { key: 'measure', label: 'Measure' },
  { key: 'condition', label: 'Condition' },
  { key: 'assess', label: 'PM assess' },
] as const;
type Tab = (typeof TABS)[number]['key'];

/** An area's rating on the visit's scale, for the area list ("2 Fair"); '' when not rated. */
/** The worst rating across these areas' surfaces, as text; '' when nothing is rated. An area's own, or its building's (block 8). */
function ratingTextOf(records: DeviceRecord[], areas: readonly DeviceRecord[], scaleKey: string): string {
  const observations = areas.flatMap((area) => {
    const visitArea = visitAreaOf(records, area.id);
    return visitArea ? observationsOf(records, visitArea.id) : [];
  });
  const rated = areaRating(observations.map((o) => ({ rating: typeof o.fields.hig_rating === 'string' ? o.fields.hig_rating : null })), scaleKey);
  return rated ? ratingText(scaleKey, rated.value) : '';
}

const areaRatingText = (records: DeviceRecord[], area: DeviceRecord, scaleKey: string): string => ratingTextOf(records, [area], scaleKey);

/** One job, entirely from device storage — works the same with or without signal. */
export default function JobPage() {
  const { jobId = '' } = useParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('measure');
  // The job's photos are read here once and handed to every area's screens (never a read per area).
  const data = useDeviceQuery(async () => ({ job: await getJob(jobId), records: await listRecords(jobId), photos: await listJobPhotos(jobId) }), [jobId]);
  const jobNumber = data?.job?.number;
  const notOnDevice = !!data && !data.job;
  const [linkFailure, setLinkFailure] = useState<string | null>(null);

  // Note the open, for the Recent jobs list on every device (#422).
  const onDevice = !!data?.job;
  useEffect(() => {
    if (onDevice) void recordOpen(jobId).catch(console.error);
  }, [jobId, onDevice]);

  // With signal, pick up changes made elsewhere since this device last had the job.
  useJobRefresh(jobNumber);

  // With a condition report, photos taken earlier (measuring, another device) become its evidence too.
  const visitId = data ? visitOf(data.records)?.id : undefined;
  // Run again when photos arrive: new rows (from here or a refresh), or an area's older list growing.
  const photoLists = data?.records.filter((r) => r.table === 'area').map((a) => String(a.fields.hig_photos ?? '').length).join(',');
  const photoRows = data?.records.filter((r) => r.table === 'photo').length;
  useEffect(() => {
    if (visitId) void addEarlierPhotos(jobId).catch(console.error);
  }, [jobId, visitId, photoLists, photoRows]);

  // Opened from an "Open in Measure2Quote" link in CRM: copy the job here first.
  useEffect(() => {
    if (!notOnDevice || !navigator.onLine) return;
    openJobById(jobId).catch((err: unknown) => setLinkFailure(err instanceof Error ? err.message : String(err)));
  }, [notOnDevice, jobId]);

  if (!data) return null;
  if (!data.job) {
    if (navigator.onLine && !linkFailure) return <p className="text-sm text-slate-600">Opening the job&hellip;</p>;
    return (
      <p className="text-sm text-slate-600">
        {linkFailure ? `${linkFailure} ` : 'This job isn’t on this device. '}
        <Link to="/" className="text-primary underline">Open it</Link> by job number while you have signal.
      </p>
    );
  }

  const job = data.job;
  const photoSources: PhotoSources = { records: data.records, onDevice: data.photos, removed: job.removedPhotoIds };
  const measure = data.records.find((r) => r.table === 'measure');
  // Buildings with their areas (lib/buildings.ts); the areas keep their job-wide order for colours and names.
  const groups = measure ? buildingGroups(data.records, measure.id) : [];
  const buildings = measure ? measureBuildings(data.records, measure.id) : [];
  const visit = visitOf(data.records);
  const scaleKey = scaleKeyOf(visit?.fields.hig_ratingscale);
  const areas = data.records
    .filter((r) => r.table === 'area')
    .sort((a, b) => Number(a.fields.hig_sortorder ?? 0) - Number(b.fields.hig_sortorder ?? 0));
  const selected = areas.find((a) => a.id === selectedId) ?? null;
  const selectedBuilding = buildings.find((b) => b.id === selectedId) ?? null;
  const condition = tab === 'condition';

  async function add(buildingId: string) {
    setSelectedId(await addArea(jobId, buildingId, nextAreaOrder(areas)));
  }

  async function addNewBuilding() {
    if (measure) setSelectedId(await addBuilding(jobId, measure.id, newBuildingFields(buildings)));
  }

  async function addDiffering(type: DeviceRecord) {
    const differing = groups.find((g) => g.building.id === type.id)?.differing.length ?? 0;
    if (measure) setSelectedId(await addBuilding(jobId, measure.id, differingFields(type, buildings, differing)));
  }

  async function moveBuilding(building: DeviceRecord, direction: -1 | 1) {
    for (const { id, order } of reorderBuildings(groups, building.id, direction)) await patchFields(id, { hig_sortorder: order });
  }

  async function dropBuilding(building: DeviceRecord) {
    await removeBuilding(building, jobNumber ?? '');
    setSelectedId(null);
  }

  /** A failure shows in the area editor, under its buttons (AreaEditor). */
  async function duplicate(area: DeviceRecord) {
    setSelectedId(await duplicateArea(area, nextAreaOrder(areas)));
  }

  async function remove(area: DeviceRecord) {
    await removeArea(area, jobNumber ?? '');
    setSelectedId(null);
  }

  /** The Condition tab of a validated (or issued) visit: its areas and buildings can't change there. */
  const conditionLocked = Boolean(condition && visit && visitLocked(visit));

  function workArea() {
    if (condition && !visit) {
      return <ConditionStart job={job} centre={job.details?.jobName ?? job.number} hasMeasure={areas.some((a) => typeof a.fields.hig_measuredhours === 'number' && a.fields.hig_measuredhours > 0)} />;
    }
    if (condition && visit && selectedId === REPORT_SECTION) return <ConditionReportPanel jobId={jobId} visit={visit} records={data!.records} />;
    if (selectedBuilding) {
      const group = groups.find((g) => g.building.id === selectedBuilding.id) ?? null;
      const at = groups.findIndex((g) => g.building.id === selectedBuilding.id);
      // On the Condition tab a validated visit's buildings and areas are locked too (#576 review); the Measure tab
      // still changes them, as the measure is priced and quoted after the visit.
      return (
        <fieldset disabled={conditionLocked} className="contents">
          <BuildingEditor
            building={selectedBuilding}
            records={data!.records}
            group={group}
            place={{ first: at <= 0, last: at === groups.length - 1 }}
            onMove={(direction) => moveBuilding(selectedBuilding, direction)}
            onAddArea={() => add(selectedBuilding.id)}
            onAddDiffering={() => addDiffering(selectedBuilding)}
            onRemove={() => dropBuilding(selectedBuilding)}
          />
        </fieldset>
      );
    }
    if (!selected) {
      return (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          {areas.length ? 'Choose an area, or a building to change it.' : 'No areas yet — add one.'}
        </p>
      );
    }
    if (condition && visit) {
      // A validated visit's ratings wait for it to be reopened (VisitPanel): every control here is disabled.
      return (
        <div className="space-y-4">
          <fieldset disabled={visitLocked(visit)} className="contents">
            <ConditionPanel visit={visit} area={selected} records={data!.records} photoSources={photoSources} />
          </fieldset>
          {/* Defects outlive validation: they're rectified and verified after the visit. */}
          <DefectsSection visit={visit} area={selected} records={data!.records} photoSources={photoSources} />
          <EvidencePointsSection visit={visit} area={selected} records={data!.records} />
        </div>
      );
    }
    return (
      <AreaEditor
        area={selected}
        mode={tab === 'assess' ? 'assess' : 'measure'}
        photoSources={photoSources}
        onDuplicate={() => duplicate(selected)}
        onRemove={() => remove(selected)}
        onAssessed={() => void advanceMeasure(measure, 'Assessing')}
      />
    );
  }

  /** One area's row in the list; its colour on the Condition tab is its place in the job-wide order, as the site plan's. */
  function areaRow(area: DeviceRecord) {
    const index = areas.indexOf(area);
    const rating = condition && visit ? areaRatingText(data!.records, area, scaleKey) : '';
    const listedHours = tab === 'assess' ? area.fields.hig_assessedhours : area.fields.hig_measuredhours;
    return (
      <button
        type="button"
        onClick={() => setSelectedId(area.id)}
        className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm outdoor:min-h-11 ${area.id === selectedId ? 'bg-orange-50 font-medium' : ''}`}
      >
        {condition && visit && <span className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: areaColour(index) }} aria-hidden="true" />}
        {/* Every tab: an area with no feature photo is easy to find (#437). */}
        <span className="min-w-0">
          <span className="block truncate">{String(area.fields.hig_name ?? '')}</span>
          <AreaPhotoMissing area={area} sources={photoSources} />
          {siteWash(area.fields).washOnly && <WashOnlyBadge className="mt-0.5" />}
        </span>
        {/* PM assess is blind (#627): the PM sees their own hours, never the measured ones. */}
        {!condition && typeof listedHours === 'number' && <span className="ml-auto shrink-0 whitespace-nowrap pl-2 text-xs tabular-nums text-slate-500">{formatHours(listedHours)}</span>}
        {condition && visit && <span className="ml-auto shrink-0 whitespace-nowrap pl-2 text-xs text-slate-500">{rating || '–'}</span>}
        {area.state === 'pending' && <CloudUpload className="h-4 w-4 shrink-0 text-amber-600" aria-label="Waiting to sync" />}
        {area.state === 'conflict' && <span className="text-xs text-red-700">{area.refusedReason ? 'not saved' : 'conflict'}</span>}
      </button>
    );
  }

  return (
    <div>
      <SyncStatus jobId={jobId} />
      <div className="mb-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1 text-sm text-slate-600">
          <ArrowLeft className="h-4 w-4" /> Jobs
        </Link>
        <div className="flex items-center gap-3">
          {measure && <PhotosFolderLink records={data.records} />}
          {visit && (
            <Link to={`/job/${jobId}/site-plan`} className="flex items-center gap-1 text-sm text-primary">
              <MapIcon className="h-4 w-4" /> Site plan
            </Link>
          )}
          {/* The desk view; priced from CRM rates, so it needs signal. */}
          {measure && (
            <Link to={`/job/${jobId}/schedule/${measure.id}`} className="flex items-center gap-1 text-sm text-primary">
              <FileText className="h-4 w-4" /> Schedule of Work
            </Link>
          )}
          {measure && (
            <Link to={`/job/${jobId}/take-off/${measure.id}`} className="hidden items-center gap-1 text-sm text-primary md:flex">
              <Table2 className="h-4 w-4" /> Pricing
            </Link>
          )}
          <SalesRiskLink jobId={jobId} jobNumber={job.number} />
          <h2 className="text-base font-semibold text-slate-900">{job.number}</h2>
        </div>
      </div>

      <div className="mb-3 flex gap-1 border-b border-slate-200" role="tablist" aria-label="View">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px min-h-11 border-b-2 px-4 text-sm ${tab === t.key ? 'border-primary font-medium text-slate-900' : 'border-transparent text-slate-600'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {condition && visit && <VisitPanel visit={visit} />}
      {condition && visit && (
        <fieldset disabled={visitLocked(visit)} className="contents">
          <ConditionScale visit={visit} records={data.records} />
        </fieldset>
      )}
      {!condition && measure && <MeasureStatusBar measure={measure} tab={tab === 'assess' ? 'assess' : 'measure'} />}

      <div className="grid gap-4 md:grid-cols-[14rem_1fr]">
        <div>
          <BuildingList
            groups={groups}
            selectedId={selectedId}
            onSelect={setSelectedId}
            renderArea={areaRow}
            onAddArea={(buildingId) => void add(buildingId)}
            canAddArea={!conditionLocked}
            showHours={tab === 'measure'}
            headingNote={condition && visit ? (_building, buildingAreas) => ratingTextOf(data.records, buildingAreas, scaleKey) : undefined}
          />
          {/* The Condition tab's Report section (#513): the desk fields and Make the condition report. */}
          {condition && visit && (
            <button
              type="button"
              onClick={() => setSelectedId(REPORT_SECTION)}
              className={`mt-2 flex min-h-11 w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-left text-sm ${selectedId === REPORT_SECTION ? 'bg-orange-50 font-medium' : ''}`}
            >
              <FileText className="h-4 w-4 text-primary" /> Condition report
            </button>
          )}
          {tab === 'measure' && (
            <AddAction onClick={() => void addNewBuilding()} disabled={!measure} className="mt-2 w-full justify-center rounded-md border border-slate-300 bg-white">
              Add building
            </AddAction>
          )}
        </div>
        {workArea()}
      </div>
    </div>
  );
}
